// App-shell — TanStack Query mutation for POST /api/v1/auth/logout.
//
// FIRST SPA consumer of the logout endpoint (Story 1.11,
// server/src/routes/auth.rs::logout at :318 — revokes the session via
// session_service::revoke and clears the httpOnly session cookie).
// Mirrors the useSignIn fetch shape (credentials: "include" +
// attachRequestIdToScope + error-envelope unwrap).
//
// The caller (AppShell's AccountMenu) invalidates + removes the
// ["account-me"] cache and navigates to / on success.

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface LogoutError {
  code: string;
  message: string;
  status: number;
}

async function postLogout(): Promise<void> {
  const response = await fetch("/api/v1/auth/logout", {
    method: "POST",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: LogoutError = {
      code: "LOGOUT_FAILED",
      message: `Logout failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
}

export function useLogout(): UseMutationResult<void, LogoutError, void> {
  return useMutation<void, LogoutError, void>({
    mutationFn: postLogout,
    retry: false,
  });
}
